import {
    PhoneIcon,
    VisitedHandshakeIcon,
    NotAvailableIcon,
    ClientSvg
} from 'assets'
import { constants, PRODUCT_CATEGORY, routes } from 'helpers/constants'
import { TargetInterface } from 'components/targetList/targetTypes'
import { PaymentProgressIcon } from 'components/targetList/PaymentProgressIcon'
import React from 'react'
import { colors } from 'styles/colors'
import { InteractionOutcomes } from 'types'
import { PaymentSummaryType } from 'pages/payment/types'
import { getRecommendedAction } from 'helpers/getRecommendedAction'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAppSelector, useAnalytics } from 'hooks'
import { ClientType } from 'storeTypes'

export const Target: React.FC<
    TargetInterface & {
        filter?: string
        selectedFilter?: string
        paymentSummary?: PaymentSummaryType
    }
> = ({ targetData, targetOnClick, paymentSummary }) => {
    const {
        isCompleted = false,
        client_code: clientCode,
        firstname,
        lastname,
        outcome,
        desired_outcome: desiredOutcome,
        phone = '',
        duplicate = false,
        isFinisher,
        product_categories: productCategories,
        village_id: villageId
    } = targetData

    const navigate = useNavigate()
    const { pathname } = useLocation()
    const { trackEvent } = useAnalytics()

    const clientDetails = useAppSelector((state) => {
        if (!clientCode || !villageId) return undefined
        const villageClients = state.villageClientList[villageId]
        if (!villageClients) return undefined
        const combinedClients: ClientType[] = [
            ...(villageClients.clientList || []),
            ...(villageClients.previousClientList || [])
        ]

        return combinedClients.find(
            (client: ClientType) => client.client_code === clientCode
        )
    })

    const isVisitedClient: boolean =
        !!outcome &&
        (Object.values(constants.visitedOutcomes).includes(outcome) ||
            outcome === InteractionOutcomes.DUPLICATE ||
            outcome === InteractionOutcomes.NOT_INTERESTED)

    const hasValidPhone = Boolean(phone?.trim() && phone.trim() !== '0')

    let phoneIconColor = ''

    if (outcome === InteractionOutcomes.DUPLICATE) {
        phoneIconColor = colors.mediumGrey
    } else if (outcome === InteractionOutcomes.NOT_INTERESTED) {
        phoneIconColor = colors.primary
    }
    const { CerealIcon, PoultryIcon } = ClientSvg

    const duplicateCancelHandler = ({
        duplicate,
        outcome
    }: {
        duplicate: boolean
        outcome: keyof typeof InteractionOutcomes | undefined
    }) => {
        if (duplicate) {
            return (
                <NotAvailableIcon height="30" width="30" color={colors.grey} />
            )
        }
        return outcome === InteractionOutcomes.NOT_INTERESTED ? (
            <NotAvailableIcon height="30" width="30" color={colors.grey} />
        ) : null
    }

    const isCanceledOrDuplicate =
        outcome?.toLocaleLowerCase() ===
            InteractionOutcomes.NOT_INTERESTED.toLowerCase() || duplicate

    const hasFollowUpOutcome =
        outcome?.toLowerCase() === InteractionOutcomes.FOLLOW_UP.toLowerCase()

    const gaugeColor = isVisitedClient ? colors.primary : colors.mediumGrey

    const recommendation = getRecommendedAction({
        outcome,
        duplicate,
        desiredOutcome,
        paymentSummary,
        shouldShowColor: isVisitedClient,
        isVisitedClient
    })

    const redirectToClientModification = () => {
        if (!clientDetails?.village_id) return
        const clientForStorage = {
            ...clientDetails,
            village_name: clientDetails.village,
            form_type: 'modification'
        }

        localStorage.setItem(
            constants.CLIENT_DETAIL_LOCAL_STORE,
            JSON.stringify(clientForStorage)
        )

        localStorage.setItem(
            constants.CLIENT_FOR_ENROLLMENT_STORE,
            JSON.stringify({
                ...clientForStorage,
                modification: constants.CLIENT_MODIFICATION_TYPES.DETAILS
            })
        )

        navigate(
            `/${routes.enroll}/${clientDetails.village_id}?from=${pathname}`
        )
    }

    const handleMissingPhoneClick = (
        event: React.MouseEvent<HTMLDivElement>
    ) => {
        event.preventDefault()
        event.stopPropagation()
        redirectToClientModification()
    }

    const renderPhoneIcon = (fillColor: string) => {
        const icon = PhoneIcon({
            color: '',
            fillColor,
            width: '30',
            height: '30'
        })

        return (
            <div
                className="target-list--phone"
                aria-label="target-client-phone"
                onClick={!hasValidPhone ? handleMissingPhoneClick : undefined}
                style={!hasValidPhone ? { cursor: 'pointer' } : undefined}
            >
                {hasValidPhone ? (
                    <a
                        href={`tel:${phone}`}
                        onClick={(event) => {
                            event.stopPropagation()
                            trackEvent({
                                category: constants.ANALYTICS.TARGET_LIST,
                                action: constants.ANALYTICS.ACTIONS.PHONE_CALL
                            })
                        }}
                        aria-label="target-client-phone-link"
                    >
                        {icon}
                    </a>
                ) : (
                    icon
                )}
            </div>
        )
    }

    const renderVisitedOrPhoneIcon = () => {
        if (isVisitedClient) {
            if (
                outcome === InteractionOutcomes.NOT_INTERESTED ||
                outcome === InteractionOutcomes.DUPLICATE
            ) {
                return renderPhoneIcon(phoneIconColor || colors.primary)
            }

            return (
                <div className="target-list--visited-icon">
                    <VisitedHandshakeIcon
                        width="41"
                        height="41"
                        color={colors.primary}
                        data-testid="visited-icon"
                    />
                </div>
            )
        }

        return renderPhoneIcon(colors.primary)
    }

    const productCategoriesConfig = {
        [PRODUCT_CATEGORY.POULTRY]: (
            <div
                className="display-flex"
                aria-label={`poultry-icon-${gaugeColor}`}
            >
                <PoultryIcon color={gaugeColor} />
            </div>
        ),
        [PRODUCT_CATEGORY.CEREAL]: (
            <div
                className="display-flex"
                aria-label={`cereal-icon-${gaugeColor}`}
            >
                <CerealIcon color={gaugeColor} />
            </div>
        )
    }

    return (
        <div
            className={`target-list--card target-list--card-${
                isCanceledOrDuplicate
                    ? 'canceled-duplicate'
                    : hasFollowUpOutcome
                    ? 'follow-up'
                    : isCompleted || isFinisher
                    ? 'completed'
                    : ''
            }`}
            aria-label="target-card"
            onClick={targetOnClick}
        >
            <div className="target-list--check-client">
                <div
                    className="target-list--check"
                    aria-label="target-list--check"
                >
                    {duplicateCancelHandler({ duplicate, outcome }) ?? (
                        <PaymentProgressIcon
                            paymentSummary={paymentSummary}
                            isVisitedClient={isVisitedClient}
                            isFinisher={isFinisher}
                        />
                    )}
                </div>
                <div>
                    <div>
                        <p className="target-list--client-type-id">
                            ID: {clientCode}
                        </p>
                        <h2 className="target-list--name">{`${firstname} ${lastname}`}</h2>
                    </div>
                    <div className="target-list--recommended-action-container">
                        {recommendation && (
                            <div
                                className={`target-list--recommended-action ${recommendation.className}`}
                                aria-label="recommended-action"
                                style={{
                                    borderRadius: '5px',
                                    backgroundColor: isVisitedClient
                                        ? recommendation.background
                                        : colors.greyLight,
                                    color: isVisitedClient
                                        ? recommendation.fontColor
                                        : ''
                                }}
                            >
                                <span
                                    className="target-list--recommended-action--icon"
                                    style={{ display: 'flex' }}
                                >
                                    {recommendation.icon}
                                </span>
                                <span className="target-list--recommended-action--label">
                                    {recommendation.label}
                                </span>
                            </div>
                        )}
                        {productCategories?.length ? (
                            <div className="target-list--product-category-container">
                                {productCategories?.map(
                                    (productCategory) =>
                                        productCategoriesConfig[
                                            productCategory.toLowerCase()
                                        ]
                                )}
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>

            <div className="target-list--status">
                <div>{renderVisitedOrPhoneIcon()}</div>
            </div>
        </div>
    )
}
